import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Sparkles, MapPin, ShieldCheck, Star, Info, Clock, Zap, Droplets, Leaf } from 'lucide-react';
import { useDomainRouter, useBranding } from '@/lib/domainRouter';
import { getTemplate } from '../lib/vbs/templates/businessTemplates';

const TRUST_ICONS: Record<string, React.ReactNode> = {
  clock: <Clock className="w-8 h-8" />,
  shield: <ShieldCheck className="w-8 h-8" />,
  map: <MapPin className="w-8 h-8" />,
  zap: <Zap className="w-8 h-8" />,
  droplet: <Droplets className="w-8 h-8" />,
  leaf: <Leaf className="w-8 h-8" />,
  star: <Star className="w-8 h-8" />,
};

const TRUST_COLORS: Record<string, string> = {
  clock: 'bg-purple-600/20 text-purple-400',
  shield: 'bg-teal-600/20 text-teal-400',
  map: 'bg-yellow-600/20 text-yellow-400',
  zap: 'bg-blue-600/20 text-blue-400',
  droplet: 'bg-sky-600/20 text-sky-400',
  leaf: 'bg-green-600/20 text-green-400',
  star: 'bg-pink-600/20 text-pink-400',
};

export const Home = () => {
  const navigate = useNavigate();
  const domainConfig = useDomainRouter();
  const branding = useBranding(domainConfig);
  const { config } = domainConfig;
  
  // Get template based on business type (or default to mowing for platform)
  const template = getTemplate(config?.biz_type ?? 'mowing');

  // If on platform domain and no config, show generic VaultStack home
  if (domainConfig.isPlatform && !config) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        {/* Hero */}
        <section className="container mx-auto px-4 py-20 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full text-white mb-8">
            <Sparkles className="w-5 h-5" />
            <span className="font-bold">Professional Business Management</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-black text-white mb-6 leading-tight">
            {branding.appName}
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto mb-10">
            {branding.tagline}
          </p>
          <div className="flex gap-4 justify-center">
            <Button
              onClick={() => navigate('/login')}
              className="h-14 px-8 bg-white text-slate-900 hover:bg-slate-100 font-black uppercase tracking-widest"
            >
              Get Started
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/booking')}
              className="h-14 px-8 border-2 border-white text-white hover:bg-white/10 font-black uppercase tracking-widest"
            >
              Book a Service
            </Button>
          </div>
        </section>

        {/* Features */}
        <section className="container mx-auto px-4 py-20">
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-white/5 border-white/10 backdrop-blur-sm">
              <CardContent className="p-6 text-center">
                <ShieldCheck className="w-12 h-12 text-teal-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Secure & Private</h3>
                <p className="text-slate-400">Your business data is isolated and encrypted</p>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border-white/10 backdrop-blur-sm">
              <CardContent className="p-6 text-center">
                <MapPin className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Custom Domain</h3>
                <p className="text-slate-400">Use your own domain or our subdomain</p>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border-white/10 backdrop-blur-sm">
              <CardContent className="p-6 text-center">
                <Zap className="w-12 h-12 text-blue-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Instant Setup</h3>
                <p className="text-slate-400">Get started in minutes, not days</p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Pricing Teaser */}
        <section className="container mx-auto px-4 py-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-white mb-4">Simple Pricing</h2>
            <p className="text-slate-400">Start free, upgrade as you grow</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card className="bg-white/5 border-white/10 backdrop-blur-sm">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-white mb-2">Free</h3>
                <p className="text-4xl font-black text-white mb-4">$0<span className="text-lg text-slate-400">/mo</span></p>
                <ul className="text-slate-400 space-y-2 mb-6">
                  <li>✓ Basic business profile</li>
                  <li>✓ Subdomain included</li>
                  <li>✓ Up to 50 customers</li>
                  <li>✓ VaultStack branding</li>
                </ul>
                <Button onClick={() => navigate('/login')} className="w-full h-12 bg-white text-slate-900 hover:bg-slate-100 font-black uppercase">
                  Start Free
                </Button>
              </CardContent>
            </Card>
            <Card className="bg-purple-600/20 border-purple-500/50 backdrop-blur-sm">
              <CardContent className="p-8">
                <div className="inline-block bg-purple-600 text-white text-xs font-bold px-3 py-1 rounded-full mb-2">MOST POPULAR</div>
                <h3 className="text-2xl font-bold text-white mb-2">Pro</h3>
                <p className="text-4xl font-black text-white mb-4">$29<span className="text-lg text-slate-400">/mo</span></p>
                <ul className="text-slate-400 space-y-2 mb-6">
                  <li>✓ Everything in Free</li>
                  <li>✓ Custom domain</li>
                  <li>✓ Unlimited customers</li>
                  <li>✓ Remove branding</li>
                </ul>
                <Button onClick={() => navigate('/login')} className="w-full h-12 bg-purple-600 hover:bg-purple-700 text-white font-black uppercase">
                  Go Pro
                </Button>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border-white/10 backdrop-blur-sm">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-white mb-2">Enterprise</h3>
                <p className="text-4xl font-black text-white mb-4">$99<span className="text-lg text-slate-400">/mo</span></p>
                <ul className="text-slate-400 space-y-2 mb-6">
                  <li>✓ Everything in Pro</li>
                  <li>✓ White label</li>
                  <li>✓ Priority support</li>
                  <li>✓ Custom integrations</li>
                </ul>
                <Button onClick={() => navigate('/login')} className="w-full h-12 bg-white text-slate-900 hover:bg-slate-100 font-black uppercase">
                  Contact Sales
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    );
  }

  // Customer-branded home page (when accessed via custom domain)
  return (
    <div className="flex flex-col gap-16 pb-24 animate-in fade-in duration-700">
      {/* Hero */}
      <section className="text-center pt-12 px-4">
        <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-1.5 rounded-full text-sm font-bold mb-6">
          <Sparkles className="w-4 h-4" />
          {config?.service_area ? `${template.badge} · ${config.service_area}` : template.badge}
        </div>
        <h1 className="text-5xl md:text-7xl font-black tracking-tighter text-slate-900 mb-6 leading-tight">
          {template.headline}{' '}
          <span className="text-purple-600">{template.headlineAccent}</span>
        </h1>
        <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-10">
          {template.tagline}
        </p>
        <Button
          onClick={() => navigate(`/${template.ctaRoute}`)}
          className="bg-[#00BFA5] hover:bg-[#00a892] text-white text-2xl h-20 px-12 rounded-2xl shadow-xl transition-all hover:scale-105 font-bold"
        >
          {template.ctaLabel}
        </Button>
        {template.pricingMode === 'both' && (
          <Button
            variant="outline"
            onClick={() => navigate('/booking')}
            className="ml-4 h-20 px-8 rounded-2xl border-2 border-slate-200 text-slate-700 font-bold text-lg"
          >
            Book Now
          </Button>
        )}
      </section>

      {/* Pricing */}
      <section className="px-4 max-w-5xl mx-auto w-full">
        <h2 className="text-3xl font-black text-center mb-4 uppercase tracking-tight">
          {template.pricingTitle}
        </h2>
        <p className="text-center text-slate-500 mb-10 flex items-center justify-center gap-2">
          <Info className="w-4 h-4 text-purple-500" /> {template.pricingSubtitle}
        </p>

        <div className="overflow-hidden rounded-3xl border-2 border-slate-100 shadow-sm bg-white mb-12">
          <table className="w-full text-left">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="p-6 font-bold text-slate-900">Service</th>
                {template.tiers.map((tier) => (
                  <th key={tier.name} className={`p-6 font-bold ${tier.color}`}>{tier.name}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {template.pricingRows.map((row) => (
                <tr key={row.label} className="hover:bg-slate-50/50 transition-colors">
                  <td className="p-6">
                    <div className="font-bold text-slate-900">{row.label}</div>
                    {row.sublabel && <div className="text-xs text-slate-500 italic">{row.sublabel}</div>}
                  </td>
                  {template.tiers.map((tier) => (
                    <td key={tier.name} className="p-6 font-black text-slate-800">
                      {typeof row.tiers[tier.name] === 'number' ? `$${row.tiers[tier.name]}` : row.tiers[tier.name]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Service Tier Cards */}
        <div className={`grid gap-8 ${template.serviceTiers.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
          {template.serviceTiers.map((tier, i) => (
            <Card key={tier.name} className={`border-2 shadow-none ${i === 0 ? 'border-slate-100' : 'border-purple-200 bg-purple-50/30'}`}>
              <CardContent className="p-8">
                <h3 className={`text-xl font-bold mb-1 ${i === 0 ? 'text-slate-900' : 'text-purple-900'}`}>{tier.name}</h3>
                <p className="text-sm text-slate-500 mb-4">{tier.description}</p>
                <ul className="space-y-3 text-slate-600">
                  {tier.includes.map((item, j) => (
                    <li key={j} className="flex items-center gap-2">
                      {j === 0 && i > 0
                        ? <Star className={`w-4 h-4 fill-purple-700 text-purple-700`} />
                        : <ShieldCheck className={`w-4 h-4 ${i === 0 ? 'text-teal-500' : 'text-purple-500'}`} />
                      }
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Trust Section */}
      <section className="bg-slate-900 text-white py-16 px-8 rounded-[3rem] mx-4 shadow-2xl">
        <div className="max-w-4xl mx-auto grid md:grid-cols-3 gap-12 text-center">
          {template.trustPoints.map((point) => (
            <div key={point.title} className="flex flex-col items-center gap-4">
              <div className={`p-4 rounded-2xl ${TRUST_COLORS[point.icon] ?? 'bg-slate-700 text-slate-300'}`}>
                {TRUST_ICONS[point.icon] ?? <Sparkles className="w-8 h-8" />}
              </div>
              <h3 className="font-bold text-lg">{point.title}</h3>
              <p className="text-slate-400 text-sm">{point.body}</p>
            </div>
          ))}
        </div>
        {config?.biz_name && (
          <p className="text-center text-slate-600 text-xs mt-12 uppercase tracking-widest font-bold">
            {config.biz_name}{config.service_area ? ` · ${config.service_area}` : ''}
            {config.is_australian_business ? ' · 🇦🇺 Proudly Australian' : ''}
          </p>
        )}
      </section>
    </div>
  );
};
